import { getTalkSession } from '@talkjs/core';
import { getChatToken } from '../api/chat';
import { handleError } from './errors';

// TalkJS stops calling onNeedToken once it rejects, so retries have to happen inside it
const TOKEN_RETRY_DELAYS_MS = [1000, 5000, 15000, 30000];

const isRetryableTokenError = (error) => error instanceof Error && (!error.statusCode || error.statusCode >= 500);

const fetchChatToken = async (attempt = 0) => {
  try {
    const response = await getChatToken();
    const json = await response.json();

    return json.data.token;
  } catch (error) {
    if (!isRetryableTokenError(error) || attempt >= TOKEN_RETRY_DELAYS_MS.length) {
      throw error;
    }

    await new Promise((resolve) => {
      setTimeout(resolve, TOKEN_RETRY_DELAYS_MS[attempt]);
    });

    return fetchChatToken(attempt + 1);
  }
};

/**
 * Returns the shared TalkJS session for the user, authenticated with a token from our API.
 * getTalkSession is backed by a registry, so <Chatbox> / <ConversationList> mounted with the same
 * appId + userId reuse this session (and its token). The handler is re-attached when TalkJS
 * replaced the session after an error.
 */
export const getChatSession = (userId) => {
  const session = getTalkSession({ appId: process.env.REACT_APP_TALKJS_APP_ID, userId: String(userId) });

  if (!session.onNeedToken) {
    session.onNeedToken = () => {
      fetchChatToken()
        .then((token) => session.setToken(token))
        .catch((error) => handleError(error, 'Chat is unavailable right now. Please refresh the page.'));
    };
  }

  return session;
};
